// Single source of truth for all portfolio content.

export const profile = {
  name: "Jemsheena M.",
  first: "Jemsheena",
  last: "M.",
  headline: "Software Engineer | Backend & Cloud",
  tagline: "Builds systems that stay up.",
  education: "B.Tech IT, 2026 Passout",
  status: "Open to Software Engineer • Backend Engineer • Cloud Engineer Roles",
  location: "Kerala, India",
  email: "jemsheenam88@gmail.com",
  phone: "+91 75928 94755",
  github: "https://github.com/jemsheena",
  githubHandle: "github.com/jemsheena",
  linkedin: "https://linkedin.com/in/jemsheena-m",
  linkedinHandle: "linkedin.com/in/jemsheena-m",
  resume: "/Jemsheena_M_Resume.pdf",
  roles: [
    "Software Engineer",
    "Backend Engineer",
    "Cloud Engineer",
  ],
  summary:
    "Software engineer building scalable backend applications with Python, AWS, Docker, Kubernetes, and PostgreSQL.",
  stats: [
    { value: "5+", label: "Systems shipped" },
    { value: "2", label: "Engineering internships" },
    { value: "50+", label: "Members led (IEEE)" },
    { value: "7.79", label: "CGPA / 10" },
  ],
};

// Slow editorial marquee keywords
export const marquee = [
  "Distributed Systems",
  "Event Sourcing",
  "Kubernetes",
  "Observability",
  "FastAPI",
  "Java 21",
  "Kafka",
  "AWS",
  "PostgreSQL",
  "CI/CD",
  "Applied AI",
  "Reliability",
];

// Numbered manifesto chapters — the engineering story
export const manifesto = {
  intro:
    "I am a software engineer passionate about backend development and cloud engineering. I enjoy designing scalable APIs, distributed services, and reliable cloud-native applications that solve real-world engineering problems.",
  chapters: [
    {
      n: "01",
      title: "Why I Build Software",
      body: "I enjoy turning ideas into production-ready software. Every project helps me improve how I design APIs, debug systems, and build reliable applications. I'm motivated by solving real engineering problems rather than simply completing projects.",
    },
    {
      n: "02",
      title: "How I Learn",
      body: "I learn by building. AI, documentation, and GitHub help me move faster, but I focus on understanding why solutions work. Debugging and iteration have been my best teachers.",
    },
    {
      n: "03",
      title: "What I'm Exploring",
      body: "I'm currently exploring backend engineering, cloud infrastructure, Kubernetes, and distributed systems while building projects that strengthen my understanding of production software.",
    },
  ],
};

// Grouped tech stack — professional grouping, no icon wall
export const techStack = [
  { group: "Languages", items: ["Python", "Java", "SQL"] },
  { group: "Backend", items: ["FastAPI", "REST APIs", "PostgreSQL"] },
  { group: "Cloud & DevOps", items: ["AWS", "Docker", "Kubernetes", "GitHub Actions"] },
  { group: "Distributed", items: ["Kafka", "Redis"] },
  { group: "Observability", items: ["Prometheus", "Grafana"] },
  { group: "Tooling", items: ["Git"] },
];

// Projects as case studies.
export const projects = [
  
  {
    id: "ai-document-assistant",
    index: "01",
    name: "Conversational Document Assistant",
    tagline: "A conversational document Q&A platform, deployed on AWS EC2.",
    year: "2025",
    status: "Deployed",
    domain: "Applied AI",
    stack: ["FastAPI", "React", "AWS EC2", "FAISS", "PostgreSQL"],
    github: "https://github.com/jemsheena/Conversational-Document-Assistant",
    live: "http://13.61.13.161/",
    summary:
      "A retrieval-augmented Q&A system that lets users ask questions over their documents and get citation-aware answers, deployed as a containerized service on AWS EC2.",
    problem:
      "Raw LLM answers over private documents can hallucinate and are hard to trust without sources. The system needed retrieval, reranking, and citations so every answer stayed grounded and verifiable.",
    motivation:
      "To take an ML idea all the way to a deployed, containerized product rather than a notebook demo.",
    architecture: [
      "Documents are chunked and embedded into a FAISS vector index.",
      "Retrieval plus reranking selects the most relevant passages before generation.",
      "FastAPI composes grounded, citation-aware answers from the retrieved context.",
      "React provides the chat UI. The app is containerized and deployed on AWS EC2.",
    ],
    challenges: [
      "Reducing hallucination while keeping answers useful and specific.",
      "Serving retrieval with acceptable latency in a containerized AWS EC2 setup.",
    ],
    solutions: [
      "Added reranking and citation-aware prompting so claims map back to source passages.",
      "Tuned chunking, chunk size, and index parameters to balance recall and latency.",
    ],
    scalability:
      "Stateless API containers can scale horizontally; PostgreSQL and file storage are separated from compute, so the app can grow without changing the retrieval pipeline.",
    performance:
      "Typical end-to-end latency is ~1.5–3s from retrieval to complete answer. Embedding queries take ~5–15 ms, FAISS search ~1–5 ms, cross-encoder rerank ~150–300 ms, first token latency ~200–500 ms, and a full 600-token response typically lands in ~1–2s. The app also tracks latency, token counts, and retrieval scores through /api/metrics.",
    impact: [
      "End-to-end RAG system running in the cloud, not just a prototype.",
      "Measured usage metrics, evaluation logic, and source-citation flow are available in the repository.",
    ],
  },
  {
    id: "saga-orchestrator",
    index: "02",
    name: "Distributed Saga Orchestrator",
    tagline: "A framework-free Java 21 engine for reliable, long-running workflows.",
    year: "2025",
    status: "Open source",
    domain: "Distributed Systems",
    stack: ["Java 21", "PostgreSQL", "Kafka", "JUnit 5"],
    github: "https://github.com/jemsheena/saga-orchestrator",
    live: null,
    summary:
      "A saga orchestration engine for coordinating multi-step business transactions across services. It combines event sourcing, CQRS, snapshotting, and transactional outbox/inbox patterns to keep workflows durable, replayable, and safe to recover.",
    problem:
      "Distributed transactions cannot rely on a single database transaction. If payment succeeds and inventory reservation fails, the system is left inconsistent. I built the coordinator myself to understand the full failure surface instead of leaning on a framework to hide it.",
    motivation:
      "Most tutorials stop at the happy path. I wanted to understand what happens when the orchestrator crashes mid-saga, a message is delivered twice, or a compensating action fails.",
    architecture: [
      "The orchestrator stores saga state as an append-only event log.",
      "PostgreSQL-backed transactional outbox/inbox keeps writes and message publication durable.",
      "Each step defines a forward action plus an idempotent compensating action.",
      "Incomplete sagas can be deterministically rehydrated from the event log.",
    ],
    challenges: [
      "Preserving reliable message delivery when a crash happens between the database write and Kafka publish.",
      "Making compensating transactions safe to retry without duplicate side effects.",
      "Rebuilding in-flight saga state deterministically after a restart.",
    ],
    solutions: [
      "Used a transactional outbox so state changes and pending messages are committed atomically, then published asynchronously.",
      "Added idempotency keys so retries and replay are safe.",
      "Rebuilt saga state from the event log so recovery is deterministic and auditable.",
    ],
    scalability:
      "The orchestrator is stateless between events; all durable state lives in Postgres. That makes it straightforward to run multiple instances and partition work by saga ID.",
    impact: [
      "Built a working saga engine without relying on an orchestration framework.",
      "Covered the failure surface with 121 automated tests, including Testcontainers-backed integration tests against PostgreSQL.",
    ],
  },
  {
    id: "kubeguardian",
    index: "03",
    name: "KubeGuardian",
    tagline: "A Kubernetes controller for self-healing workloads.",
    year: "2025",
    status: "Open source",
    domain: "Cloud Infrastructure",
    stack: ["Python", "Kubernetes", "Prometheus", "Grafana"],
    github: "https://github.com/jemsheena/kubeguardian",
    live: null,
    summary:
      "KubeGuardian is a custom Kubernetes controller that watches workloads in a local kind cluster, detects unhealthy pod states, and takes automated remediation actions with full observability through Prometheus metrics and Grafana dashboards.",
    problem:
      "Kubernetes' default restart behavior is intentionally generic. I wanted a rule-based remediation layer that could respond differently to CrashLoopBackOff, long-lived Pending pods, and Failed workloads, while making every action easy to inspect.",
    motivation:
      "To learn the controller/reconciliation model, the core pattern behind Kubernetes operators, by building something operationally useful.",
    architecture: [
      "Watches pod status in a kind cluster.",
      "Detects container CrashLoopBackOff states, Pending pods that exceed a configured threshold, and Failed pods.",
      "For CrashLoopBackOff, deletes the affected pod so its controller can recreate it.",
      "For Pending and Failed workloads, resolves the owning Deployment by walking ownerReferences, typically Pod -> ReplicaSet -> Deployment, then triggers a rollout restart.",
      "Applies a per-workload cooldown window to prevent repeated healing loops.",
      "Exposes Prometheus metrics for incidents detected, incidents resolved, and healing actions.",
      "Publishes Grafana dashboards for live visibility.",
      "Logs incidents as JSON Lines for auditability.",
    ],
    challenges: [
      "Avoiding heal loops where a workload is restarted repeatedly without recovering.",
      "Resolving the correct owning Deployment across different ownership chains.",
    ],
    solutions: [
      "Added a configurable cooldown window, defaulting to 300 seconds, between heals per workload.",
      "Traversed ownerReferences to reliably identify the target Deployment before issuing a rollout restart.",
    ],
    scalability:
      "The current implementation targets a local kind cluster only. It does not yet include cloud IAM, ingress, or multi-cluster support. RBAC is least privilege: watch/get/list/delete on pods, get on ReplicaSets, and get/patch on Deployments.",
    impact: [
      "A working self-healing controller with live Prometheus and Grafana observability, validated against demo CrashLoopBackOff and Pending workloads in a kind cluster.",
      "Unit tests cover failure detection, heal-action routing, cooldown behavior, and Deployment ownership resolution.",
    ],
  },
];

export const experience = [
  {
    id: "dromolys",
    period: "Sep 2025 — Dec 2025",
    role: "Software Engineering Intern",
    company: "DromoLys R&D Pvt. Ltd.",
    location: "India",
    achievements: [
      "Developed and deployed backend applications on AWS (EC2, Fargate, S3) with PostgreSQL, supporting feature development and deployment workflows.",
      "Monitored cloud infrastructure and worked across REST API development and CI/CD pipelines to improve service reliability and deployment stability.",
    ],
    stack: ["AWS", "PostgreSQL", "CI/CD", "Backend"],
  },
  {
    id: "ieee-security",
    period: "Jun 2025 — Jul 2025",
    role: "Cybersecurity Intern",
    company: "IEEE (Remote)",
    location: "Remote",
    achievements: [
      "Performed simulated security assessments to identify OWASP Top 10 vulnerabilities — including injection, broken authentication, and security misconfiguration.",
    ],
    stack: ["Security", "OWASP", "Web"],
  },
];

export const recognition = {
  intro: "Where I led, wrote, volunteered, and kept learning beyond the classroom.",
  leadership: [
    {
      id: "ieee-chair",
      title: "Chairperson — IEEE Communications Society (ComSoc), GEC Palakkad",
      body: "Led a team of 50+ student members and organized 10+ technical events, strengthening student engagement and technical initiatives. Also contributed as a Content Team Member for IEEE GEC Palakkad.",
    },
    {
      id: "iste-treasurer",
      title: "Treasurer & IT Forum Member— ISTE GEC Palakkad",
      body: "",
    },
  
    {
      id: "alumni-coordinator",
      title: "IT Coordinator — Alumni Students Forum",
      body: "",
    },
  ],
  awards: [
    {
      id: "best-writer",
      title: "Best Content Writer Award — IEEE GEC Palakkad",
      body: "",
    },
  ],
  certifications: [
    { id: "ibm-sql", issuer: "IBM", title: "SQL and Relational Databases" },
    { id: "hackerrank-se", issuer: "HackerRank", title: "Software Engineer Certificate" },
    { id: "nptel-cloud", issuer: "NPTEL", title: "Cloud Computing Fundamentals" },
  ],
};

export const navLinks = [
  { id: "hero", label: "Home" },
  { id: "work", label: "Work" },
  { id: "about", label: "About" },
  { id: "stack", label: "Stack" },
  { id: "experience", label: "Experience" },
  { id: "contact", label: "Contact" },
];

// Editorial marquee ribbons (two layers, opposite directions).
export const marqueeLines = {
  top: [
    "BUILD",
    "SCALE",
    "SOLVE",
    "LEARN",
    "CREATE",
    "ITERATE",
    "OPTIMIZE",
    "SHIP",
  ],
  bottom: [
    "SYSTEMS",
    "BACKEND",
    "CLOUD",
    "AI",
    "PERFORMANCE",
    "RELIABILITY",
    "SECURITY",
    "AUTOMATION",
  ],
};

// Interactive technology ecosystem — nodes + relationships.
// x/y are percentages inside the graph viewport (0..100).
export const techGraph = {
  nodes: [
    { id: "python", label: "Python", group: "lang", x: 78, y: 30, hub: true },
    { id: "sql", label: "SQL", group: "data", x: 64, y: 16 },
    { id: "fastapi", label: "FastAPI", group: "backend", x: 70, y: 50, hub: true },
    { id: "rest", label: "REST APIs", group: "backend", x: 88, y: 74 },
    { id: "postgres", label: "PostgreSQL", group: "data", x: 62, y: 58, hub: true },
    { id: "redis", label: "Redis", group: "data", x: 80, y: 88 },
    { id: "java", label: "Java", group: "lang", x: 44, y: 48, hub: true },
    { id: "kafka", label: "Kafka", group: "distributed", x: 28, y: 54, hub: true },
    { id: "git", label: "Git", group: "devops", x: 46, y: 24 },
    { id: "aws", label: "AWS", group: "cloud", x: 10, y: 26, hub: true },
    { id: "docker", label: "Docker", group: "devops", x: 28, y: 8 },
    { id: "k8s", label: "Kubernetes", group: "devops", x: 18, y: 12, hub: true },
    { id: "actions", label: "GitHub Actions", group: "devops", x: 50, y: 14 },
    { id: "prom", label: "Prometheus", group: "observability", x: 12, y: 70 },
    { id: "grafana", label: "Grafana", group: "observability", x: 4, y: 84 },
  ],
  edges: [
    ["python", "fastapi"],
    ["fastapi", "rest"], ["fastapi", "postgres"],
    ["sql", "postgres"],
    ["java", "kafka"], ["kafka", "postgres"],
    ["git", "actions"],
    ["actions", "docker"], ["docker", "aws"],
    ["docker", "k8s"], ["aws", "k8s"],
    ["k8s", "prom"], ["prom", "grafana"],
  ],
};

export const groupColors = {
  lang: "82 80% 58%",
  backend: "82 80% 58%",
  frontend: "190 80% 60%",
  data: "265 70% 68%",
  ai: "145 70% 58%",
  distributed: "35 90% 62%",
  devops: "200 80% 62%",
  cloud: "200 80% 62%",
  observability: "0 75% 64%",
  testing: "215 15% 62%",
};
